import {
  doc,
  collection,
  serverTimestamp,
  writeBatch,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../lib/firebase";
import type {
  Audience,
  TravelItem,
  TravelItemCreate,
  BulkCreateTravelItemsInput,
  BulkCreateTravelItemsResponse,
  BulkResolveTravelItemsInput,
  BulkResolveTravelItemsResponse,
} from "@event-planner/shared";
import {
  resolveAudienceTarget,
  isAnonymousAudience,
} from "./audienceService";

const bulkCreateTravelItemsCallable = httpsCallable<
  BulkCreateTravelItemsInput,
  BulkCreateTravelItemsResponse
>(functions, "bulkCreateTravelItems");

export async function bulkCreateTravelItems(
  input: BulkCreateTravelItemsInput,
): Promise<BulkCreateTravelItemsResponse> {
  const result = await bulkCreateTravelItemsCallable(input);
  return result.data;
}

const bulkResolveTravelItemsCallable = httpsCallable<
  BulkResolveTravelItemsInput,
  BulkResolveTravelItemsResponse
>(functions, "bulkResolveTravelItems");

export async function bulkResolveTravelItems(
  input: BulkResolveTravelItemsInput,
): Promise<BulkResolveTravelItemsResponse> {
  const result = await bulkResolveTravelItemsCallable(input);
  return result.data;
}

/**
 * Direct Firestore CRUD for travelItems (ADR-018: single-doc writes
 * don't need callables). The `reminderSentAt` idempotency guard and
 * `targetedPersonIds` audience denorm are both CF-managed — blocked at
 * the rule layer for client writes. Create sends `targetedPersonIds: []`;
 * the `onTravelItemWritten` trigger populates it (ADR-137).
 *
 * Audience composition (ADR-120): the form passes `pickedAudienceIds[]`
 * (named audiences plus personal audience IDs derived from picked
 * emails). The service either reuses a single audience or writes an
 * anonymous composition alongside the travel item in one
 * Firestore writeBatch.
 */

export type CreateTravelItemInput = Omit<TravelItemCreate, "audienceId"> & {
  pickedAudienceIds: string[];
};

export type UpdateTravelItemInput = Partial<
  Omit<CreateTravelItemInput, "eventId">
>;

export async function createTravelItem(
  input: CreateTravelItemInput,
): Promise<string> {
  const { eventId, pickedAudienceIds, ...rest } = input;
  const target = resolveAudienceTarget(eventId, pickedAudienceIds);

  const travelRef = doc(collection(db, "travelItems"));
  const batch = writeBatch(db);

  batch.set(travelRef, {
    ...rest,
    eventId,
    audienceId:
      target.mode === "reuse" ? target.audienceId : target.audienceRef.id,
    reminderSentAt: null,
    targetedPersonIds: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  if (target.mode === "compose") {
    batch.set(target.audienceRef, target.audienceData);
  }

  await batch.commit();
  return travelRef.id;
}

export async function updateTravelItem(
  eventId: string,
  travelItemId: string,
  input: UpdateTravelItemInput,
): Promise<void> {
  const { pickedAudienceIds, ...rest } = input;
  const travelRef = doc(db, "travelItems", travelItemId);
  const batch = writeBatch(db);

  if (pickedAudienceIds === undefined) {
    batch.update(travelRef, {
      ...rest,
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
    return;
  }

  const target = resolveAudienceTarget(eventId, pickedAudienceIds);

  const travelSnap = await getDoc(travelRef);
  const prevAudienceId = travelSnap.exists()
    ? (travelSnap.data() as TravelItem).audienceId
    : null;

  const newAudienceId =
    target.mode === "reuse" ? target.audienceId : target.audienceRef.id;

  batch.update(travelRef, {
    ...rest,
    audienceId: newAudienceId,
    updatedAt: serverTimestamp(),
  });

  if (target.mode === "compose") {
    batch.set(target.audienceRef, target.audienceData);
  }

  if (prevAudienceId && prevAudienceId !== newAudienceId) {
    const prevAudSnap = await getDoc(doc(db, "audiences", prevAudienceId));
    if (prevAudSnap.exists()) {
      const prevAud = prevAudSnap.data() as Audience;
      if (isAnonymousAudience(prevAud)) {
        batch.delete(prevAudSnap.ref);
      }
    }
  }

  await batch.commit();
}

/**
 * Direct delete on the travel item. Anonymous-composition cleanup is
 * the caller's responsibility — read the doc first and use a writeBatch
 * with this delete plus the audience delete if you want them atomic.
 * Most callers should use the dedicated cascade helper if needed; for
 * now this stays a single-doc delete to match the existing API.
 */
export async function deleteTravelItem(travelItemId: string): Promise<void> {
  await deleteDoc(doc(db, "travelItems", travelItemId));
}
